<template>
    <Spinner v-if="displaySpinner" />
    <Page>
        <template #eyebrow>
            <Eyebrow icon="pi pi-directions">Gateway</Eyebrow>
        </template>
        <template #title>
            <Title :level="1" class="page-title">
                {{ gatewayName }}
            </Title>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="gateway?.identity"
                :entityId="gateway.identity"
                :entityType="EntityType.GATEWAY"
            />
            <template v-if="isArchived">
                <Button
                    class="p-button-secondary"
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content && isEditing"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateGatewayDescription"
                />
            </template>
        </template>
        <Section editable>
            <TextAppArtefact
                v-if="gateway?.identity && description"
                v-model="description"
                :entityId="gateway.identity"
                :entityType="EntityType.GATEWAY"
                :isEditing="isEditing"
            />
        </Section>
        <Section v-if="gateway?.entity_refs?.length || isEditing" editable>
            <template #title>Linked Domain Objects</template>
            <BusinessEntityRefs
                v-if="gateway?.identity"
                :elementId="gateway.identity"
                :entityRefs="gateway.entity_refs ?? []"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";

import BusinessEntityRefs from "@/components/BusinessEntityRefs.vue";
import Eyebrow from "@/components/Eyebrow.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Title from "@/components/Title.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { baseSuccessToastMessage } from "@/primevue/init";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useGatewayManager, useGatewayStore } from "@/stores/gateway-store";

const toast = useToast();
const { deleteGateway } = useGatewayStore();
const { isEditing } = useEditManager();
const { isFetching, description, gateway } = useGatewayManager();
const { generateContent } = useGenerateDescriptionDialog();

const confirm = useConfirm();

const displaySpinner = ref(false);

const isArchived = computed(() => {
    return gateway.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete gateway. Gateway is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this gateway?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteGateway();
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Gateway has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete gateway",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const gatewayName = computed(() => {
    if (!gateway.value) {
        return "...";
    }

    return gateway.value.name || gateway.value.bpmn_id;
});

const generateGatewayDescription = async () => {
    if (!gateway.value) {
        return;
    }
    await generateContent(
        gateway.value.identity,
        EntityType.GATEWAY,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.page-title {
    display: flex;
    align-items: center;
    gap: 6px;

    .p-tag {
        margin-left: 0.5rem;
    }
}
</style>
